import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Feather, MessageCircle, Loader2, Send, Quote, X, Highlighter, Languages, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import {
    shareNovel, shareChapters, shareComments, addShareComment,
} from "@/lib/api";
import { sanitizeHtml } from "@/lib/sanitize";
import { translate } from "@/lib/i18n";

const ANCHOR_MAX = 300;

// Build chapter HTML with anchored passages wrapped in clickable <mark> tags.
// Operates on the already-sanitized HTML string and only wraps text that
// literally exists outside of tags, so nothing new/unsafe is injected.
function buildMarkedHtml(html, comments, flashId) {
    let out = html || "";
    for (const c of comments) {
        const anchor = (c.anchor || "").trim();
        if (!anchor) continue;
        const idx = out.indexOf(anchor);
        if (idx === -1) continue;
        const before = out.slice(0, idx);
        if (before.lastIndexOf("<") > before.lastIndexOf(">")) continue; // inside a tag
        const matched = out.slice(idx, idx + anchor.length);
        const cls = `reader-anchor-highlight${c.id === flashId ? " reader-anchor-flash" : ""}`;
        const mark = `<mark id="anchor-${c.id}" data-anchor="${c.id}" class="${cls}">${matched}</mark>`;
        out = before + mark + out.slice(idx + anchor.length);
    }
    return out;
}

export default function ReaderPage() {
    const { token } = useParams();
    const [lang, setLangState] = useState(() => {
        const saved = localStorage.getItem("dd.lang");
        if (saved) return saved;
        return (navigator.language || "en").toLowerCase().startsWith("es") ? "es" : "en";
    });
    const setLang = (next) => { setLangState(next); localStorage.setItem("dd.lang", next); };
    const t = useCallback((k, v) => translate(lang, k, v), [lang]);
    const [novel, setNovel] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [comments, setComments] = useState([]);
    const [activeChapter, setActiveChapter] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);
    const [reviewerName, setReviewerName] = useState(() => localStorage.getItem("reviewer_name") || "");
    const [draft, setDraft] = useState("");
    const [posting, setPosting] = useState(false);
    const [panelOpen, setPanelOpen] = useState(false);
    const [pendingAnchor, setPendingAnchor] = useState("");
    const [selBtn, setSelBtn] = useState(null); // {x, y, text} | null
    const [flashId, setFlashId] = useState(null);
    const contentRef = useRef(null);

    useEffect(() => {
        (async () => {
            try {
                const [n, chs, cs] = await Promise.all([
                    shareNovel(token), shareChapters(token), shareComments(token),
                ]);
                setNovel(n); setChapters(chs); setComments(cs);
                setActiveChapter(chs[0] || null);
            } catch (e) {
                setNotFound(true);
            } finally { setLoading(false); }
        })();
    }, [token]);

    const chapterComments = useMemo(
        () => comments.filter(c => activeChapter ? c.chapter_id === activeChapter.id : !c.chapter_id),
        [comments, activeChapter]
    );

    // Marked HTML is computed during render so anchor highlights survive every
    // React re-render (panel toggles, typing, etc.).
    const markedHtml = useMemo(() => {
        const base = sanitizeHtml(activeChapter?.content) || `<em>${t("rd.emptyChapter")}</em>`;
        return buildMarkedHtml(base, chapterComments, flashId);
    }, [activeChapter, chapterComments, flashId, t]);

    // Capture text selection inside the chapter body to offer "Add note here".
    const onContentMouseUp = useCallback(() => {
        const sel = window.getSelection();
        if (!sel || sel.isCollapsed) { setSelBtn(null); return; }
        const text = sel.toString().trim();
        const container = contentRef.current;
        if (!text || !container) { setSelBtn(null); return; }
        // ensure selection is within the chapter content
        if (!container.contains(sel.anchorNode) || !container.contains(sel.focusNode)) {
            setSelBtn(null); return;
        }
        const rect = sel.getRangeAt(0).getBoundingClientRect();
        setSelBtn({
            x: Math.min(rect.left + rect.width / 2, window.innerWidth - 90),
            y: Math.max(rect.top - 8, 48),
            text: text.slice(0, ANCHOR_MAX),
        });
    }, []);

    useEffect(() => {
        const clear = () => setSelBtn(null);
        window.addEventListener("scroll", clear, true);
        return () => window.removeEventListener("scroll", clear, true);
    }, []);

    function startAnchoredNote() {
        if (!selBtn) return;
        setPendingAnchor(selBtn.text);
        setPanelOpen(true);
        setSelBtn(null);
        window.getSelection()?.removeAllRanges();
    }

    function scrollToAnchor(id) {
        const exists = chapterComments.some(c => c.id === id && (c.anchor || "").trim() && (sanitizeHtml(activeChapter?.content) || "").includes(c.anchor.trim()));
        if (!exists) { toast.info(t("rd.notOnView")); return; }
        setFlashId(id);
    }

    // Scroll to and flash the anchored passage once the marked HTML re-renders.
    useEffect(() => {
        if (!flashId) return;
        const raf = requestAnimationFrame(() => {
            const el = document.getElementById(`anchor-${flashId}`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
        });
        const clear = setTimeout(() => setFlashId(null), 1700);
        return () => { cancelAnimationFrame(raf); clearTimeout(clear); };
    }, [flashId]);

    async function postComment(e) {
        e.preventDefault();
        if (!reviewerName.trim() || !draft.trim()) {
            toast.error(t("rd.needNameNote"));
            return;
        }
        localStorage.setItem("reviewer_name", reviewerName.trim());
        setPosting(true);
        try {
            const c = await addShareComment(token, {
                name: reviewerName.trim(),
                text: draft.trim(),
                chapter_id: activeChapter?.id || "",
                anchor: pendingAnchor || "",
            });
            setComments([c, ...comments]);
            setDraft("");
            setPendingAnchor("");
            toast.success(t("rd.shared"));
        } catch { toast.error(t("rd.postFail")); }
        setPosting(false);
    }

    const chapterIndex = chapters.findIndex(c => c.id === activeChapter?.id);
    const goToIndex = useCallback((i) => {
        if (i < 0 || i >= chapters.length) return;
        setActiveChapter(chapters[i]);
        setSelBtn(null);
        setFlashId(null);
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [chapters]);

    // Swipe left → next chapter, swipe right → previous (mobile reading).
    const touchRef = useRef(null);
    function onTouchStart(e) {
        if (e.touches.length !== 1) { touchRef.current = null; return; }
        touchRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
    function onTouchEnd(e) {
        const start = touchRef.current;
        touchRef.current = null;
        if (!start) return;
        const sel = window.getSelection();
        if (sel && !sel.isCollapsed) return; // don't hijack text selection
        const t1 = e.changedTouches[0];
        const dx = t1.clientX - start.x;
        const dy = t1.clientY - start.y;
        if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
        goToIndex(dx < 0 ? chapterIndex + 1 : chapterIndex - 1);
    }

    // Desktop: ← / → arrow keys turn chapters (ignored while typing a note).
    useEffect(() => {
        function onKey(e) {
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            const el = document.activeElement;
            const tag = el?.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || el?.isContentEditable) return;
            if (e.key === "ArrowRight") { e.preventDefault(); goToIndex(chapterIndex + 1); }
            else if (e.key === "ArrowLeft") { e.preventDefault(); goToIndex(chapterIndex - 1); }
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [chapterIndex, goToIndex]);

    if (loading) {
        return (
            <div className="h-screen flex flex-col items-center justify-center text-muted-foreground">
                <div className="flex items-center gap-2 font-heading text-2xl mb-3">
                    <Feather className="w-5 h-5 text-primary" /> Day Dreamer
                </div>
                <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> {t("rd.loading")}</div>
            </div>
        );
    }
    if (notFound) {
        return (
            <div className="h-screen flex flex-col items-center justify-center text-muted-foreground p-8">
                <div className="flex items-center gap-2 font-heading text-3xl mb-3">
                    <Feather className="w-5 h-5 text-primary" /> Day Dreamer
                </div>
                <p>{t("rd.invalid")}</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background text-foreground">
            <header className="border-b border-border/60 bg-card/40 backdrop-blur-md sticky top-0 z-10">
                <div className="max-w-5xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <Feather className="w-4 h-4 text-primary shrink-0" />
                        <div className="min-w-0">
                            <div className="font-heading text-xl leading-tight truncate" data-testid="reader-novel-title">{novel.title}</div>
                            <div className="ui-label leading-tight">{novel.genre || t("rd.readonly")}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1" data-testid="reader-lang-toggle">
                            <Button
                                data-testid="reader-lang-en"
                                variant={lang === "en" ? "default" : "outline"}
                                size="sm" className="h-8 px-2.5 text-[11px]"
                                onClick={() => setLang("en")}
                            >EN</Button>
                            <Button
                                data-testid="reader-lang-es"
                                variant={lang === "es" ? "default" : "outline"}
                                size="sm" className="h-8 px-2.5 text-[11px]"
                                onClick={() => setLang("es")}
                            >ES</Button>
                        </div>
                        <Button
                            data-testid="reader-toggle-comments"
                            variant="outline" size="sm"
                            onClick={() => setPanelOpen(o => !o)}
                        >
                            <MessageCircle className="w-4 h-4" />
                            {t("rd.notes")} {chapterComments.length > 0 && <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded-full bg-primary/15 text-primary">{chapterComments.length}</span>}
                        </Button>
                    </div>
                </div>
            </header>

            <div className="max-w-5xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-8 py-10">
                {/* Chapter list */}
                <aside className="hidden lg:block">
                    <div className="ui-label mb-3">{t("rd.chapters")}</div>
                    <ul className="space-y-0.5 sticky top-24">
                        {chapters.map(c => (
                            <li key={c.id}>
                                <button
                                    data-testid={`reader-chapter-${c.id}`}
                                    onClick={() => setActiveChapter(c)}
                                    className={`w-full text-left px-3 py-1.5 rounded-md text-sm transition-colors ${activeChapter?.id === c.id ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-muted/50"}`}>
                                    <span className="font-heading text-base block leading-tight">{c.title}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </aside>

                {/* Main */}
                <main className="min-w-0" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                    {novel.synopsis && activeChapter?.id === chapters[0]?.id && (
                        <Card className="p-5 mb-8 bg-card/60">
                            <div className="ui-label mb-2">{t("rd.synopsis")}</div>
                            <p className="font-editor text-sm leading-relaxed text-foreground/85 italic">{novel.synopsis}</p>
                        </Card>
                    )}
                    {activeChapter ? (
                        <article>
                            <h1 className="font-heading text-4xl lg:text-5xl font-light tracking-tight mb-4">{activeChapter.title}</h1>
                            {chapters.length > 1 && (
                                <div className="lg:hidden flex items-center gap-2 text-xs text-muted-foreground mb-4" data-testid="reader-mobile-chapter-pos">
                                    <span className="px-2 py-0.5 rounded-full bg-secondary text-foreground/80">{t("rd.chapterOf", { n: chapterIndex + 1, total: chapters.length })}</span>
                                    <span className="flex items-center gap-1"><ChevronLeft className="w-3 h-3" /> {t("rd.swipeHint")} <ChevronRight className="w-3 h-3" /></span>
                                </div>
                            )}
                            <p className="text-xs text-muted-foreground mb-8 flex items-center gap-1.5">
                                <Highlighter className="w-3.5 h-3.5 text-primary" />
                                {t("rd.selectHint")}
                            </p>
                            <div
                                ref={contentRef}
                                key={activeChapter.id}
                                data-testid="reader-chapter-content"
                                onMouseUp={onContentMouseUp}
                                onClick={(e) => {
                                    const m = e.target.closest?.("mark[data-anchor]");
                                    if (m) { setPanelOpen(true); scrollToAnchor(m.getAttribute("data-anchor")); }
                                }}
                                className="prose-quill editor-content"
                                dangerouslySetInnerHTML={{ __html: markedHtml }}
                            />
                            {/* prev/next */}
                            <div className="flex items-center justify-between mt-12 pt-6 border-t border-border/60">
                                {chapterIndex > 0 ? (
                                    <Button data-testid="reader-prev" variant="ghost"
                                        onClick={() => goToIndex(chapterIndex - 1)}>{t("rd.prev")}</Button>
                                ) : <div />}
                                {chapterIndex < chapters.length - 1 ? (
                                    <Button data-testid="reader-next"
                                        onClick={() => goToIndex(chapterIndex + 1)}>{t("rd.next")}</Button>
                                ) : <div />}
                            </div>
                        </article>
                    ) : <p className="text-muted-foreground italic">{t("rd.noChapters")}</p>}
                </main>
            </div>

            {/* Floating "Add note here" on text selection */}
            {selBtn && (
                <button
                    data-testid="reader-add-note-here"
                    onMouseDown={(e) => { e.preventDefault(); startAnchoredNote(); }}
                    style={{ position: "fixed", left: selBtn.x, top: selBtn.y, transform: "translate(-50%, -100%)", zIndex: 40 }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs shadow-lg hover:brightness-110 transition"
                >
                    <Quote className="w-3.5 h-3.5" /> {t("rd.addNoteHere")}
                </button>
            )}

            {/* Slide-over notes panel */}
            <div className={`fixed top-0 right-0 h-full w-full sm:w-[400px] bg-card/95 backdrop-blur-md border-l border-border z-20 transition-transform duration-300 ${panelOpen ? "translate-x-0" : "translate-x-full"}`}>
                <div className="h-full flex flex-col">
                    <div className="p-5 border-b border-border/60 flex items-center justify-between">
                        <div>
                            <div className="ui-label mb-1">{t("rd.notesFor")}</div>
                            <div className="font-heading text-lg leading-tight">{activeChapter?.title || t("rd.thisManuscript")}</div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setPanelOpen(false)}>{t("common.close")}</Button>
                    </div>
                    <ScrollArea className="flex-1 p-5">
                        <div className="space-y-3">
                            {chapterComments.length === 0 && (
                                <p className="text-xs text-muted-foreground italic">{t("rd.notesEmpty")}</p>
                            )}
                            {chapterComments.map(c => (
                                <Card
                                    key={c.id}
                                    data-testid={`reader-comment-${c.id}`}
                                    className={`p-3 bg-background/70 ${c.anchor ? "cursor-pointer hover:border-primary/50 transition-colors" : ""}`}
                                    onClick={() => c.anchor && scrollToAnchor(c.id)}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-heading text-base">{c.name}</span>
                                        <span className="ui-label">{new Date(c.created_at).toLocaleDateString()}</span>
                                    </div>
                                    {c.anchor && (
                                        <div className="mt-1.5 flex items-start gap-1.5 text-xs italic text-primary/90 border-l-2 border-primary/50 pl-2" data-testid={`reader-comment-anchor-${c.id}`}>
                                            <Quote className="w-3 h-3 mt-0.5 shrink-0" />
                                            <span className="line-clamp-2">&ldquo;{c.anchor}&rdquo;</span>
                                        </div>
                                    )}
                                    <p className="text-sm font-editor leading-relaxed mt-1.5 text-foreground/90 whitespace-pre-wrap">{c.text}</p>
                                </Card>
                            ))}
                        </div>
                    </ScrollArea>
                    <form onSubmit={postComment} className="p-5 border-t border-border/60 space-y-2 bg-card">
                        {pendingAnchor && (
                            <div className="flex items-start gap-1.5 text-xs italic text-primary/90 bg-primary/10 rounded-md px-2.5 py-2" data-testid="reader-pending-anchor">
                                <Quote className="w-3 h-3 mt-0.5 shrink-0" />
                                <span className="line-clamp-2 flex-1">&ldquo;{pendingAnchor}&rdquo;</span>
                                <button type="button" data-testid="reader-clear-anchor" onClick={() => setPendingAnchor("")} className="text-muted-foreground hover:text-foreground">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        )}
                        <Input data-testid="reader-name-input" value={reviewerName} onChange={(e) => setReviewerName(e.target.value)} placeholder={t("rd.yourName")} maxLength={80} />
                        <Textarea data-testid="reader-comment-input" value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} placeholder={pendingAnchor ? t("rd.notePhAnchor") : t("rd.notePhChapter")} maxLength={4000} />
                        <Button data-testid="reader-submit-comment" type="submit" className="w-full" disabled={posting}>
                            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {t("rd.send")}
                        </Button>
                    </form>
                </div>
            </div>
        </div>
    );
}
